import React, { useState } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Button,
  Select,
  message,
  Row,
  Col,
  Space,
  Card,
  DatePicker,
  Switch,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { CheckCircleOutlined, DeploymentUnitOutlined } from '@ant-design/icons';
import { useConfig } from '../../contexts/ConfigContext';
import { useCompany } from '../../contexts/CompanyContext';
import { useRegister } from '../../contexts/RegisterContext';
import useAuth from '../../hooks/useAuth';
import { useSecureRequest } from '../../hooks/useSecureRequest';
import { useItems, useStoreFetch } from '../../hooks/useInventory';
import { useCycleTraceability } from '../../hooks/useProductionCycles';
import CycleLogAttachments, { AttachmentItem } from './CycleLogAttachments';
import fileStorageService, {
  CYCLE_ATTACHMENTS_BUCKET,
} from '../../services/fileStorageService';
import { useUser } from '../../contexts/UserContext';
import {
  getItemPurchaseTaxInfo,
  calculateInputItemTax,
  CycleLogInputsSection,
  CycleLogOutputsSection,
  CycleLogConfirmModal,
} from './cycle-log';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

export interface CycleLogFormProps {
  cycle: any;
  onClose: () => void;
  initialLogType?: string;
}

const CycleLogForm: React.FC<CycleLogFormProps> = ({
  cycle,
  onClose,
  initialLogType,
}) => {
  const [form] = Form.useForm();
  const { config } = useConfig();
  const { company } = useCompany();
  const { register } = useRegister();
  const { user } = useUser();
  const { tokenData } = useAuth.useToken(
    'authCsrfToken',
    config.session,
    config.key
  );
  const { securePost } = useSecureRequest();
  const [loading, setLoading] = useState(false);

  // We load inventory items to allow choosing products or services
  const { data: inventoryData } = useItems(tokenData, {
    company: company.current?.uid,
    module: config.module,
  });
  const inventoryItems = inventoryData?.itemList || [];
  const [dynamicItems, setDynamicItems] = useState<any[]>([]);
  const allItems = [...inventoryItems, ...dynamicItems];

  // Fetch storehouses
  const { data: storeData } = useStoreFetch(tokenData, {
    company: company.current?.uid,
    all: true,
  });
  const storehouses =
    storeData?.storehouse || storeData?.storehouseList || [];

  // Traceability link to group members
  const { links } = useCycleTraceability(tokenData, { cycle_id: cycle?.id });
  const activeLink = links && links.length > 0 ? links[0] : null;
  const linkedGroup = activeLink?.traceable_instance_groups;
  const activeMembersCount =
    linkedGroup?.traceable_instance_group_members?.filter(
      (m: any) => m.is_active
    )?.length || 0;

  const logType = Form.useWatch('log_type', form);
  const inputsData = Form.useWatch('inputs', form) || [];
  const outputsData = Form.useWatch('outputs', form) || [];
  const storehouseId = Form.useWatch('storehouse_id', form);
  const applyCatalogTaxes = Form.useWatch('apply_catalog_taxes', form) ?? true;
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingValues, setPendingValues] = useState<any>(null);

  // Auto-select storehouse if there is only 1 and it hasn't been set yet
  React.useEffect(() => {
    if (
      storehouses.length === 1 &&
      !storehouseId &&
      (logType === 'INPUT_APPLICATION' ||
        logType === 'OUTPUT_HARVEST' ||
        logType === 'SUPPLY_PURCHASE')
    ) {
      form.setFieldsValue({ storehouse_id: storehouses[0].id });
    }
  }, [storehouses.length, logType, storehouseId, form]);

  const calculateItemTax = React.useCallback(
    (item: any) => {
      const taxConfig = getItemPurchaseTaxInfo(
        item?.element_id,
        allItems,
        register.taxesOptions || []
      );
      return calculateInputItemTax(
        item,
        logType,
        applyCatalogTaxes,
        taxConfig
      );
    },
    [allItems, register.taxesOptions, logType, applyCatalogTaxes]
  );

  const subtotalInputs = (inputsData || []).reduce(
    (sum: number, item: any) =>
      sum + (Number(item?.quantity) || 0) * (Number(item?.unit_price) || 0),
    0
  );
  const totalTaxInputs = (inputsData || []).reduce(
    (sum: number, item: any) => sum + calculateItemTax(item).taxAmount,
    0
  );
  const totalInputs =
    logType === 'SUPPLY_PURCHASE' || logType === 'SERVICE_EXPENSE'
      ? subtotalInputs + totalTaxInputs
      : subtotalInputs;
  const totalOutputs = (outputsData || []).reduce(
    (sum: number, item: any) =>
      sum + (item?.quantity || 0) * (item?.unit_price || 0),
    0
  );

  const handleSubmit = async (values: any) => {
    setLoading(true);
    const uploadedStoragePaths: string[] = [];

    try {
      const uploadDeferredAttachments = async (
        attachments: AttachmentItem[] | undefined,
        prefix: string
      ) => {
        if (!attachments || attachments.length === 0) return [];
        const processed: any[] = [];
        for (const att of attachments) {
          if (att.rawFile) {
            const timestamp = Date.now();
            const sanitizedName = att.rawFile.name.replaceAll(
              /[^a-zA-Z0-9._-]/g,
              '_'
            );
            const storagePath = `${company.current?.uid}/${cycle.uid}/logs/${prefix}_${timestamp}_${sanitizedName}`;
            const { url, storagePath: finalPath } =
              await fileStorageService.uploadFile(
                tokenData,
                CYCLE_ATTACHMENTS_BUCKET,
                storagePath,
                att.rawFile
              );
            uploadedStoragePaths.push(finalPath);
            processed.push({
              file_name: att.file_name,
              file_type: att.file_type,
              file_url: url,
              storage_path: finalPath,
              description: att.description,
              uploaded_by_user_id: user?.id || null,
              log_input_id: att.log_input_id || null,
            });
          } else {
            processed.push({
              file_name: att.file_name,
              file_type: att.file_type,
              file_url: att.file_url,
              storage_path: att.storage_path,
              description: att.description,
              uploaded_by_user_id: user?.id || null,
              log_input_id: att.log_input_id || null,
            });
          }
        }
        return processed;
      };

      // 1. Process deferred general attachments
      const processedGeneralAttachments = await uploadDeferredAttachments(
        values.general_attachments,
        'general'
      );

      let inputsToSend: any[] = [];
      let outputsToSend: any[] = [];

      if (
        [
          'INPUT_APPLICATION',
          'SUPPLY_PURCHASE',
          'LABOR_LOG',
          'SERVICE_EXPENSE',
        ].includes(values.log_type)
      ) {
        if (!values.inputs || values.inputs.length === 0) {
          const emptyMsg =
            values.log_type === 'SUPPLY_PURCHASE'
              ? 'Debe agregar al menos un insumo comprado'
              : values.log_type === 'LABOR_LOG'
              ? 'Debe agregar al menos una labor o jornal'
              : values.log_type === 'SERVICE_EXPENSE'
              ? 'Debe agregar al menos un servicio o gasto'
              : 'Debe agregar al menos un insumo';
          throw new Error(emptyMsg);
        }
        for (let i = 0; i < values.inputs.length; i++) {
          const inp = values.inputs[i];
          if (!inp?.element_id) {
            throw new Error(
              `La fila ${i + 1} no tiene un producto o servicio seleccionado`
            );
          }
          if (!inp.quantity || inp.quantity <= 0) {
            throw new Error(
              `La fila ${i + 1} debe tener una cantidad mayor a 0`
            );
          }
          const processedInputAttachments = await uploadDeferredAttachments(
            inp.attachments,
            `input_${i + 1}`
          );
          let moduleOptionsToSend: any[] = [];
          let taxAmountToSend = 0;
          let taxesListToSend: any[] = [];

          if (
            (values.log_type === 'SUPPLY_PURCHASE' ||
              values.log_type === 'SERVICE_EXPENSE') &&
            (values.apply_catalog_taxes ?? true)
          ) {
            const taxInfo = calculateItemTax(inp);
            if (taxInfo.taxConfig) {
              moduleOptionsToSend = [
                {
                  option_id: taxInfo.taxConfig.id,
                  sub_type: 'PURCHASE_TAX',
                  is_active: true,
                },
              ];
              taxAmountToSend = taxInfo.taxAmount;
              taxesListToSend = [
                {
                  id: taxInfo.taxConfig.id,
                  key: taxInfo.taxConfig.key,
                  tax_amount: taxInfo.taxAmount,
                  description:
                    taxInfo.taxConfig.string_value || taxInfo.taxConfig.key,
                },
              ];
            }
          }

          const inputItem = allItems.find((it: any) => it.id === inp.element_id);
          const resolvedContactId =
            values.log_type === 'SUPPLY_PURCHASE' ||
            values.log_type === 'SERVICE_EXPENSE' ||
            values.log_type === 'LABOR_LOG'
              ? values.contact_id
              : inp.contact_id;

          inputsToSend.push({
            ...inp,
            name: inp.name || inputItem?.name,
            item_name: inp.item_name || inp.name || inputItem?.name,
            item_code:
              inp.item_code || inputItem?.custom_id || inputItem?.sku || '',
            item_description:
              inp.item_description || inputItem?.description || '',
            contact_id: resolvedContactId,
            attachments: processedInputAttachments,
            module_options: moduleOptionsToSend,
            tax_amount: taxAmountToSend,
            taxes_list: taxesListToSend,
          });
        }
      }

      if (values.log_type === 'OUTPUT_HARVEST') {
        if (!values.outputs || values.outputs.length === 0) {
          throw new Error('Debe agregar al menos un producto a recolectar');
        }
        for (let i = 0; i < values.outputs.length; i++) {
          const out = values.outputs[i];
          if (!out?.element_id) {
            throw new Error(
              `El producto en la fila ${i + 1} no tiene un elemento seleccionado`
            );
          }
          if (!out.quantity || out.quantity <= 0) {
            throw new Error(
              `El producto en la fila ${i + 1} debe tener una cantidad mayor a 0`
            );
          }
        }
        outputsToSend = (values.outputs || []).map((out: any) => {
          const outItem = allItems.find((it: any) => it.id === out.element_id);
          return {
            ...out,
            name: out.name || outItem?.name,
            item_name: out.item_name || out.name || outItem?.name,
            item_code:
              out.item_code || outItem?.custom_id || outItem?.sku || '',
            item_description:
              out.item_description || outItem?.description || '',
          };
        });
      }

      // 2. Prepare payload
      const payload = {
        cycle_id: cycle.id,
        stage_id: values.stage_id,
        log_type: values.log_type,
        log_date: values.log_date
          ? values.log_date.format()
          : dayjs().startOf('minute').format(),
        notes: values.notes,
        quantity_change: values.quantity_change,
        metrics: values.metrics || {},
        attachments: processedGeneralAttachments,
        inputs: inputsToSend,
        outputs: outputsToSend,
        storehouse_id:
          values.log_type === 'INPUT_APPLICATION' ||
          values.log_type === 'OUTPUT_HARVEST' ||
          values.log_type === 'SUPPLY_PURCHASE'
            ? values.storehouse_id
            : undefined,
        contact_id:
          values.log_type === 'SUPPLY_PURCHASE' ||
          values.log_type === 'SERVICE_EXPENSE' ||
          values.log_type === 'LABOR_LOG'
            ? values.contact_id
            : undefined,
        apply_catalog_taxes:
          values.log_type === 'SUPPLY_PURCHASE' ||
          values.log_type === 'SERVICE_EXPENSE'
            ? (values.apply_catalog_taxes ?? true)
            : undefined,
        total_tax_amount:
          values.log_type === 'SUPPLY_PURCHASE' ||
          values.log_type === 'SERVICE_EXPENSE'
            ? totalTaxInputs
            : 0,
        cycle_uid: cycle.uid,
        cycle_category: cycle.cycle_category,
        cycle_company_id: cycle.company_id,
        cycle_name: cycle.name,
        cycle_current_quantity: cycle.current_quantity ?? null,
        cycle_initial_quantity: cycle.initial_quantity ?? null,
        created_by_user_id: user?.id,
        created_by_user_name: user?.name,
        created_by_user_document: user?.document_number,
        approved_by_user_id: user?.id,
        approved_by_user_name: user?.name,
        approved_by_user_document: user?.document_number,
        contact_name:
          values.log_type === 'SUPPLY_PURCHASE' ||
          values.log_type === 'SERVICE_EXPENSE' ||
          values.log_type === 'LABOR_LOG'
            ? values.contact_name
            : undefined,
      };

      const res = await securePost('/manufacturing/create-log', {
        company_uid: company.current?.uid,
        log: payload,
        csrfToken: tokenData?.token?.uid,
      });

      if (res?.log) {
        if (
          values.propagate_to_traceability &&
          linkedGroup?.id &&
          activeMembersCount > 0
        ) {
          try {
            const inputNames = (inputsToSend || [])
              .map((i: any) => {
                const found = allItems.find((item: any) => item.id === i.element_id);
                return found?.name || 'Insumo/Labor';
              })
              .filter(Boolean)
              .join(', ');

            const logTemplate = {
              title:
                values.propagation_title ||
                values.notes ||
                `Aplicación en ciclo: ${cycle.name}`,
              notes: `Registrado automáticamente desde Ciclo Productivo: "${cycle.name}". Insumos/Labores: ${
                inputNames || 'N/A'
              }. Observaciones: ${values.notes || 'Ninguna'}`,
              reference_type: 'PRODUCTION_CYCLE_LOG',
              reference_id: res.log.id,
              log_date: values.log_date
                ? values.log_date.toISOString()
                : new Date().toISOString(),
              metrics: values.metrics || {},
              created_by_user_id: user?.id,
            };

            await securePost('/traceability/create-logs-bulk', {
              company_uid: company.current?.uid,
              group_id: linkedGroup.id,
              log_template: logTemplate,
              csrfToken: tokenData?.token?.uid,
            });
            message.info(
              `Evento propagado a la hoja de vida de los ${activeMembersCount} miembros del grupo`
            );
          } catch (propErr) {
            console.error(
              'Error propagando a trazabilidad individual:',
              propErr
            );
          }
        }

        message.success('Evento registrado correctamente');
        onClose();
      } else {
        throw new Error(res?.message || 'Error al registrar evento');
      }
    } catch (err: any) {
      if (uploadedStoragePaths.length > 0) {
        await fileStorageService
          .deleteFile(tokenData, {
            bucket: CYCLE_ATTACHMENTS_BUCKET,
            paths: uploadedStoragePaths,
          })
          .catch(() => {});
      }
      message.error(err.message || 'Error en la operación');
    } finally {
      setLoading(false);
      setShowConfirmModal(false);
    }
  };

  const onFinish = (values: any) => {
    if (
      [
        'INPUT_APPLICATION',
        'OUTPUT_HARVEST',
        'SUPPLY_PURCHASE',
        'MORTALITY_LOSS',
        'UNIT_ADJUSTMENT',
        'LABOR_LOG',
        'SERVICE_EXPENSE',
      ].includes(values.log_type)
    ) {
      setPendingValues(values);
      setShowConfirmModal(true);
    } else {
      handleSubmit(values);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={{
        log_type: initialLogType || 'OBSERVATION',
        log_date: dayjs().startOf('minute'),
        general_attachments: [],
        inputs: [{}],
        apply_catalog_taxes: true,
      }}
    >
      <Row gutter={config.isMobile ? 8 : 16}>
        <Col xs={24} sm={12}>
          <Form.Item
            name="log_type"
            label="Tipo de Evento"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="INPUT_APPLICATION">
                🌾 Consumo / Aplicación de Insumos
              </Option>
              <Option value="LABOR_LOG">
                🧑‍🌾 Jornales y Labores (Mano de Obra)
              </Option>
              <Option value="SERVICE_EXPENSE">
                🚚 Servicios y Gastos Externos (Fletes, etc.)
              </Option>
              <Option value="SUPPLY_PURCHASE">🛒 Compra de Insumos</Option>
              <Option value="OUTPUT_HARVEST">🥦 Producción / Cosecha</Option>
              <Option value="MORTALITY_LOSS">
                💀 Mortalidad / Pérdida de Capacidad
              </Option>
              <Option value="UNIT_ADJUSTMENT">
                ⚖️ Ajuste de Lote (Ampliación, Nacimientos)
              </Option>
              <Option value="OBSERVATION">
                👁️ Observación General / Métricas
              </Option>
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="log_date"
            label="Fecha y Hora"
            rules={[{ required: true }]}
          >
            <DatePicker
              showTime={{ minuteStep: 15 }}
              format="YYYY-MM-DD HH:mm"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
      </Row>

      {cycle.has_stages &&
        cycle.production_cycle_stages &&
        cycle.production_cycle_stages.length > 0 && (
          <Form.Item name="stage_id" label="Etapa del Ciclo (Opcional)">
            <Select allowClear placeholder="Seleccionar etapa actual">
              {cycle.production_cycle_stages.map((st: any) => (
                <Option key={st.id} value={st.id}>
                  {st.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

      {/* INPUTS Section */}
      {(logType === 'INPUT_APPLICATION' ||
        logType === 'SUPPLY_PURCHASE' ||
        logType === 'LABOR_LOG' ||
        logType === 'SERVICE_EXPENSE') && (
        <CycleLogInputsSection
          form={form}
          logType={logType}
          inputsData={inputsData}
          subtotalInputs={subtotalInputs}
          totalTaxInputs={totalTaxInputs}
          totalInputs={totalInputs}
          storehouses={storehouses}
          storehouseId={storehouseId}
          applyCatalogTaxes={applyCatalogTaxes}
          allItems={allItems}
          dynamicItems={dynamicItems}
          setDynamicItems={setDynamicItems}
          calculateItemTax={calculateItemTax}
          register={register}
          cycleUid={cycle.uid}
          isMobile={config.isMobile}
        />
      )}

      {/* OUTPUTS Section */}
      {logType === 'OUTPUT_HARVEST' && (
        <CycleLogOutputsSection
          form={form}
          outputsData={outputsData}
          totalOutputs={totalOutputs}
          storehouses={storehouses}
          storehouseId={storehouseId}
          allItems={allItems}
          dynamicItems={dynamicItems}
          setDynamicItems={setDynamicItems}
          register={register}
          isMobile={config.isMobile}
        />
      )}

      {/* QUANTITY CHANGE Section */}
      {(logType === 'MORTALITY_LOSS' || logType === 'UNIT_ADJUSTMENT') && (
        <Card
          title={
            logType === 'MORTALITY_LOSS'
              ? 'Bajas/Mortalidad'
              : 'Ajuste de Capacidad del Lote'
          }
          style={{
            marginBottom: 16,
            borderColor: logType === 'MORTALITY_LOSS' ? 'red' : 'orange',
          }}
          size="small"
        >
          <Form.Item
            name="quantity_change"
            label="Cambio en la Capacidad/Tamaño del Lote (Ej: -5 si se perdieron árboles, +10 si amplió)"
            rules={[{ required: true }]}
            help={`La capacidad actual del lote calculada es: ${
              cycle.current_quantity || cycle.initial_quantity || 0
            }`}
          >
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
        </Card>
      )}

      {/* NOTES AND METRICS */}
      <Form.Item
        name="notes"
        label={
          logType === 'OBSERVATION'
            ? 'Detalles de Observación'
            : 'Notas del Sistema / Justificación'
        }
      >
        <TextArea rows={3} />
      </Form.Item>

      {/* PROPAGATION TO TRACEABILITY MEMBERS */}
      {linkedGroup && activeMembersCount > 0 && (
        <Card
          size="small"
          style={{
            marginBottom: 16,
            background: '#f9f0ff',
            borderColor: '#d3adf7',
            borderRadius: 6,
          }}
          bodyStyle={{ padding: config.isMobile ? '10px 8px' : '12px 16px' }}
        >
          <Row align="middle" justify="space-between" gutter={[12, 8]}>
            <Col xs={19} sm={20}>
              <Space align="start">
                <DeploymentUnitOutlined
                  style={{ color: '#722ed1', fontSize: 20, marginTop: 2 }}
                />
                <div>
                  <Text
                    strong
                    style={{
                      color: '#531dab',
                      display: 'block',
                      fontSize: config.isMobile ? 12 : 13,
                    }}
                  >
                    Propagar a Hoja de Vida Individual
                  </Text>
                  <Text
                    type="secondary"
                    style={{ fontSize: config.isMobile ? 11 : 12 }}
                  >
                    Se registrará en el historial de los{' '}
                    <strong>{activeMembersCount} miembros activos</strong> de{' '}
                    <strong>{linkedGroup.name}</strong>.
                  </Text>
                </div>
              </Space>
            </Col>
            <Col xs={5} sm={4} style={{ textAlign: 'right' }}>
              <Form.Item
                name="propagate_to_traceability"
                valuePropName="checked"
                noStyle
                initialValue={false}
              >
                <Switch checkedChildren="Sí" unCheckedChildren="No" />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      )}

      <Card
        title={
          logType === 'SUPPLY_PURCHASE'
            ? '📎 Factura / Comprobante de Compra (PDF o Foto)'
            : logType === 'LABOR_LOG'
            ? '📎 Recibo / Comprobante de Jornal (PDF o Foto)'
            : logType === 'SERVICE_EXPENSE'
            ? '📎 Factura / Comprobante de Gasto o Servicio (PDF o Foto)'
            : '📎 Adjuntos Generales del Evento'
        }
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Form.Item name="general_attachments" valuePropName="value" noStyle>
          <CycleLogAttachments cycleUid={cycle.uid} />
        </Form.Item>
      </Card>

      <div
        style={{
          textAlign: 'right',
          marginTop: 20,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
        }}
      >
        <Button
          onClick={onClose}
          style={{ flex: config.isMobile ? '1' : 'initial' }}
        >
          Cancelar
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          loading={loading}
          icon={<CheckCircleOutlined />}
          style={{
            flex: config.isMobile ? '2' : 'initial',
            fontWeight: 600,
          }}
        >
          Guardar Evento
        </Button>
      </div>

      <CycleLogConfirmModal
        visible={showConfirmModal}
        onConfirm={handleSubmit}
        onCancel={() => setShowConfirmModal(false)}
        loading={loading}
        pendingValues={pendingValues}
        allItems={allItems}
        storehouses={storehouses}
        cycle={cycle}
        isMobile={config.isMobile}
      />
    </Form>
  );
};

export default CycleLogForm;
